import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationCheckService } from '../auth/authentication-check.service';
import { ProgramService } from '../program/program.service';
import { ToastService } from '@packages/ng-shared-services';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  /**
   * SuperAdmin HTTP Interceptor
   *
   * Token Selection Logic:
   * - Master Token (auth-token): Used for pre-program APIs and when X-Program-Id is 'master_admin'
   * - Program Token (device_token): Used for most APIs inside /superAdmin/* after program selection
   *
   * The interceptor automatically selects the correct token:
   * - For specific endpoints (account-manager APIs) → ALWAYS uses master token
   * - If X-Program-Id header is 'master_admin' → uses master token
   * - If programToken exists → uses device_token (program-specific APIs)
   * - If programToken is null → uses master token (pre-program APIs)
   *
   * Master Token Only Endpoints:
   * - /admin/get-manager
   * - /admin/create-manager
   * - /admin/update-manager
   * - /admin/delete-manager
   *
   * Dynamic Token Selection (based on X-Program-Id header):
   * - /user-role/* endpoints → master token if X-Program-Id='master_admin', program token otherwise
   *
   * Note: X-Program-Id header (using Program UID) is sent by each service to route to correct schema.
   */
  constructor(
    private auth: AuthenticationCheckService,
    private programService: ProgramService,
    private toast: ToastService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get both tokens
    const programToken = this.programService.programToken(); // device_token from program-login
    const masterToken = this.auth.getAuthToken(); // auth-token from login

    // List of endpoints that should ALWAYS use master token, never program token
    // EXPERIMENTAL: Removed account-manager endpoints to allow program token usage
    const masterTokenOnlyEndpoints: string[] = [
      // Account manager endpoints now use program token when programUid is available
      // '/admin/get-manager',
      // '/admin/create-manager',
      // '/admin/update-manager',
      // '/admin/delete-manager',
    ];

    // Endpoints that should NOT have program_id added automatically
    // (e.g., program management endpoints that operate ON programs)
    const excludeProgramIdEndpoints: string[] = [
      '/programs/delete-programs',
      '/programs/update-program-status',
      '/programs/program-login',
      '/programs/create-programs',
      '/programs/update-programs',
      '/programs/get-programs',
    ];

    // Check if this request should use master token only
    const useMasterTokenOnly = masterTokenOnlyEndpoints.some(endpoint =>
      request.url.includes(endpoint)
    );

    // Check if X-Program-Id header is 'master_admin'
    const programIdHeader = request.headers.get('X-Program-Id');
    const useMasterTokenForProgramId = programIdHeader === 'master_admin';

    // Priority:
    // - If endpoint requires master token → use master token
    // - If X-Program-Id is 'master_admin' → use master token
    // - Otherwise: Program Token (device_token) > Master Token (auth-token)
    let selectedToken: string | null = null;
    
    if (useMasterTokenOnly || useMasterTokenForProgramId) {
      selectedToken = masterToken;
    } else {
      selectedToken = programToken || masterToken;
    }

    // Check if this endpoint should skip automatic program_id injection
    const shouldExcludeProgramId = excludeProgramIdEndpoints.some(endpoint =>
      request.url.includes(endpoint)
    );

    // Add program_id=1 to all requests (unless excluded)
    if (!shouldExcludeProgramId) {
      // For GET requests: Add/override program_id=1 to URL query parameters
      if (request.method === 'GET') {
        try {
          // Parse URL - handle both relative and absolute URLs
          let urlObj: URL;
          if (request.url.startsWith('http://') || request.url.startsWith('https://')) {
            // Absolute URL
            urlObj = new URL(request.url);
          } else {
            // Relative URL - use a dummy base
            urlObj = new URL(request.url, 'http://dummy');
          }
          
          // Always set program_id=1 (will override if exists with different value)
          urlObj.searchParams.set('program_id', '1');
          
          // Reconstruct the URL
          let finalUrl: string;
          if (request.url.startsWith('http://') || request.url.startsWith('https://')) {
            // Return absolute URL
            finalUrl = urlObj.toString();
          } else {
            // Return relative URL (pathname + search + hash)
            finalUrl = urlObj.pathname + urlObj.search + urlObj.hash;
          }
          
          request = request.clone({ url: finalUrl });
        } catch (e) {
          // If URL parsing fails, skip program_id injection
          console.warn('Failed to parse URL for program_id injection:', request.url, e);
        }
      }

      // For POST/PUT/PATCH/DELETE requests: Add program_id: 1 to payload
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(request.method) && request.body) {
        // Only add if body is an object and doesn't already have program_id
        if (typeof request.body === 'object' && !Array.isArray(request.body)) {
          // Handle FormData separately
          if (request.body instanceof FormData) {
            // Only add if program_id is not already in FormData
            if (!request.body.has('program_id')) {
              const newFormData = new FormData();
              // Copy existing data
              request.body.forEach((value, key) => {
                newFormData.append(key, value);
              });
              // Add program_id
              newFormData.append('program_id', '1');
              request = request.clone({ body: newFormData });
            }
          } else if (!('program_id' in request.body)) {
            // Regular JSON body
            request = request.clone({
              body: { ...request.body, program_id: 1 },
            });
          }
        }
      }
    }

    // Add Authorization token with Bearer prefix if available
    if (selectedToken) {
      // Trim any whitespace and ensure Bearer prefix is added once
      const cleanToken = selectedToken.trim();
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });
    }

    // Add Accept header
    request = request.clone({
      headers: request.headers.set('Accept', 'application/json'),
    });

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        // Check if this is an HTTP response (not progress event, etc.)
        if (event instanceof HttpResponse) {
          // Extract response body
          const responseBody = event.body;

          // Check if component wants to handle success toast itself
          const skipSuccessToast = request.headers.get('X-Skip-Success-Toast') === 'true';

          // Handle API-level success/failure (response body contains success field)
          if (responseBody && typeof responseBody === 'object') {
            // Check nested data structure: response.data.success or response.success
            const apiSuccess =
              responseBody.data?.success !== undefined
                ? responseBody.data.success
                : responseBody.success;
            const apiMessage =
              responseBody.data?.message || responseBody.message || responseBody.data?.error;

            // If API explicitly returns success: false, throw error
            if (apiSuccess === false && apiMessage) {
              // Clean up message
              let cleanMessage = apiMessage;
              if (typeof cleanMessage === 'string') {
                cleanMessage = cleanMessage.replace(/^["']|["']$/g, '').trim();
                cleanMessage = cleanMessage.replace(/\\"/g, '"');
                cleanMessage = cleanMessage.replace(/\\\\/g, '\\');
              }

              // Show error toast (unless component opted out)
              const skipErrorToast = request.headers.get('X-Skip-Error-Toast') === 'true';
              if (!skipErrorToast) {
                this.toast.error(cleanMessage);
              }

              // Throw error so component's catchError handler is triggered
              throw new HttpErrorResponse({
                error: { message: cleanMessage },
                headers: event.headers,
                status: event.status,
                statusText: event.statusText,
                url: event.url || undefined,
              });
            }

            // For successful POST/PUT/DELETE/PATCH requests, show success message
            if (
              apiSuccess === true &&
              apiMessage &&
              !skipSuccessToast &&
              ['POST', 'PUT', 'DELETE', 'PATCH'].includes(request.method)
            ) {
              // Clean up message
              let cleanMessage = apiMessage;
              if (typeof cleanMessage === 'string') {
                cleanMessage = cleanMessage.replace(/^["']|["']$/g, '').trim();
                cleanMessage = cleanMessage.replace(/\\"/g, '"');
                cleanMessage = cleanMessage.replace(/\\\\/g, '\\');
              }
              this.toast.success(cleanMessage);
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        let toasterMessage = '';

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          toasterMessage = error.error.message;
        } else {
          // Server-side error - extract message with multiple fallback paths
          toasterMessage =
            error.error?.data?.message || // Path 1: {data: {message: "..."}}
            error.error?.message || // Path 2: {message: "..."}
            error.error?.data?.error || // Path 3: {data: {error: "..."}}
            error.error?.error || // Path 4: {error: "..."}
            error.error?.data?.errors?.[0] || // Path 5: {data: {errors: ["..."]}}
            error.error?.errors?.[0] || // Path 6: {errors: ["..."]}
            error.statusText || // Path 7: HTTP status text
            error.message || // Path 8: Error message
            'An error occurred'; // Final fallback

          // If we got an object instead of a string, try to extract message from it
          if (typeof toasterMessage === 'object' && toasterMessage !== null) {
            toasterMessage = (toasterMessage as any).message || JSON.stringify(toasterMessage);
          }
        }

        // Clean up message - remove extra quotes if present
        // Backend sometimes sends: "\"Required fields missing: Outlet Code\""
        if (typeof toasterMessage === 'string') {
          // Remove leading/trailing quotes and unescape
          toasterMessage = toasterMessage.replace(/^["']|["']$/g, '').trim();
          // Handle escaped quotes
          toasterMessage = toasterMessage.replace(/\\"/g, '"');
        }

        console.error('HTTP Error:', error.status, toasterMessage);

        // Check if component wants to handle error toast itself
        // Components can add header: { 'X-Skip-Error-Toast': 'true' }
        const skipErrorToast = request.headers.get('X-Skip-Error-Toast') === 'true';

        // Handle authentication errors - logout user with toast
        if (this.isAuthenticationError(error, toasterMessage)) {
          if (!skipErrorToast) {
            this.toast.error('Session expired. Please login again.');
          }
          setTimeout(() => {
            this.auth.logout();
            this.router.navigateByUrl('/signin', { replaceUrl: true });
          }, 500);
        } else {
          // Show error toast for all other errors (unless component opts out)
          if (!skipErrorToast) {
            this.toast.error(toasterMessage);
          }
        }

        return throwError(() => error);
      }),
      finalize(() => {
        // Loader dismiss logic can be added here if needed
      })
    );
  }

  /**
   * Check if error is related to authentication
   */
  private isAuthenticationError(error: HttpErrorResponse, message: string): boolean {
    // Status code checks
    if (error.status === 401 || error.status === 403) {
      return true;
    }

    // Message-based checks (case-insensitive)
    const authErrorMessages = [
      'no access to this module',
      'unauthorized user access',
      'access token is not set permission denied',
      'something wrong with request',
      'token expired',
      'invalid token',
    ];

    const normalizedMessage = message.toLowerCase().replace(/["']/g, '');
    return authErrorMessages.some(errMsg => normalizedMessage.includes(errMsg));
  }
}
